import PortOpeningRule from './PortOpeningRule';
import WorkflowInstance from '../WorkflowInstance';
import User from '../User';
import { WorkflowState } from '../enums/WorkflowState';
import { Environment } from '../enums/Environment';
import { authenticatedUser } from '../../authentication/authModule';
import { UserRole } from '../enums/UserRole';
import { PortOpeningRequestsDirection } from '../enums/POR/PortOpeningRequestsDirection';
import SubscriptionRequest from './SubscriptionRequest';
import CartaIdComponent from '../viewModels/CartaId/CartaIdComponent';
import CartaId from './CartaId';
import ApprovalContact, { fromUser, fromEmails, fromAppEndorser } from '../interfaces/ApprovalContact';
import { ApprovalContactLabels } from '../ApprovalContactLabels';

export default class PortOpeningRequest {

	id!: number;

	portOpeningRules!: PortOpeningRule[];

	workflowInstance!: WorkflowInstance;

	direction!: PortOpeningRequestsDirection;

	environment!: Environment;

	contactUser!: User;

	isEmergency!: boolean;

	incidentNumber!: string;

	submitDate!: Date;

	businessJustification!: string;

	comments!: string;

	approvingUser!: User;

	delegatedApprovingUser!: User;

	hasDelegationApprover!: boolean;

	subscriptionRequestId!: number;
	
	subscriptionRequestName: string;

	customerCoordination!: boolean;

	custodianApproval!: boolean;

	isAttestation: boolean;

	escalated: boolean;

	cartaId!: CartaId[];
	
	appEndorser!: CartaIdComponent;

	currentApproverDistributionList: string[];



	constructor(obj: any) {
		Object.assign(this, obj);
		this.workflowInstance = new WorkflowInstance(obj.workflowInstance);
		this.direction = new PortOpeningRequestsDirection().fromValue(obj.direction?.value);
		const customFields = new CartaIdComponent(obj.appEndorser).fromUser(obj.appEndorser);
		this.appEndorser = new CartaIdComponent({
					...obj.appEndorser,
					customFields: customFields,
				});
	}

	implementationTicket(): string {
		const implementationState = this.workflowInstance.workflowInstanceStates
			.reverse()
			.find(state => state.ticketId !== null);
		return implementationState?.ticketId || '';
	}

	isCancellable(): boolean {
		return (
			authenticatedUser.userId === this.workflowInstance.createdBy.id &&
			[
				WorkflowState.Draft.value,
				WorkflowState.PendingSubscriptionOwnerApproval.value,
				WorkflowState.PendingForDOAGApproval.value,
				WorkflowState.PendingForCloudNetworkApproval.value,
				WorkflowState.PendingForPolicyTeamApproval.value,
				WorkflowState.Review.value,
			].includes(this.workflowInstance.currentWorkflowInstanceState.workflowState.value)
		);
	}

	currentApprovers(subscription?: SubscriptionRequest): ApprovalContact[] {
		let approvers: ApprovalContact[] = [];
		const distributionList = fromEmails(this.currentApproverDistributionList);

		switch (this.workflowInstance.currentWorkflowInstanceState.workflowState?.value) {
			case WorkflowState.PendingSubscriptionOwnerApproval.value: {
				const users = [subscription?.owner];
				if (this.custodianApproval) users.push(subscription?.custodian, subscription?.custodianTwo);
				const uniqueUsers = users.filter(
					(user, index) => user && users.findIndex((other) => other?.id === user.id) === index
				);
				approvers = uniqueUsers.map((user) => fromUser(user));
				break;
			}
			case WorkflowState.PendingForPolicyTeamApproval.value:
				approvers = [{ name: ApprovalContactLabels.NetworkPolicyTeam, email: distributionList }];
				break;
			case WorkflowState.PendingSolutionArchitectureApproval.value:
				approvers = [{ name: ApprovalContactLabels.NetworkPolicySolutionArchitects, email: distributionList }];
				break;
			case WorkflowState.PendingForCyberApproval.value:
				approvers = [{ name: ApprovalContactLabels.CyberSecurityTeam, email: distributionList }];
				break;
			case WorkflowState.PendingForDOAGApproval.value:
				approvers = [
					fromUser(this.approvingUser),
					this.hasDelegationApprover ? fromUser(this.delegatedApprovingUser) : null,
				];
				break;
			case WorkflowState.PendingApplicationApproval.value:
				approvers = [fromAppEndorser(this.appEndorser)];
				break;
		}

		return approvers.filter((approver) => !!approver?.name);
	}

	canViewDOAGApproval(): boolean {
		return authenticatedUser.userId === this.approvingUser?.id;
	}

	canHaveDOAGApproval(): boolean {
		const currentState = this.workflowInstance.currentWorkflowInstanceState.workflowState.value;
		return (
			currentState === WorkflowState.PendingForDOAGApproval.value &&
			(authenticatedUser.userId === this.approvingUser?.id || authenticatedUser.userId === this.delegatedApprovingUser?.id)
		);
	}

	canHaveApplicationApproval(): boolean {
		const currentState = this.workflowInstance.currentWorkflowInstanceState.workflowState.value;
		return (
			currentState === WorkflowState.PendingApplicationApproval.value &&
			(authenticatedUser.userId === this.appEndorser?.id)
		);
	}

	IsAppEndorserApproval(): boolean {
		const direction = this.direction;
		return (
			direction === PortOpeningRequestsDirection.SubscriptionToInternet &&
			(this.appEndorser != null)
		);
	}

	canHaveTechnicalDecision(): boolean {
		const currentState = this.workflowInstance.currentWorkflowInstanceState.workflowState.value;
		return (
			(
				currentState === WorkflowState.PendingForCloudNetworkApproval.value
				&& authenticatedUser.isInRole(UserRole.SubToOnPremTechnicalReviewer)
				
			) ||
			(
				currentState === WorkflowState.PendingForPolicyTeamApproval.value
				&& authenticatedUser.isInRole(UserRole.SubToInternetTechnicalReviewer)
			) ||
			(
				currentState === WorkflowState.PendingSolutionArchitectureApproval.value
				&& authenticatedUser.isInRole(UserRole.PORSubToInternetSolArqReviewer)
			) ||
			(
				currentState === WorkflowState.PendingForCyberApproval.value
				&& authenticatedUser.isInRole(UserRole.PORSubToInternetCyberReviewer)
			) ||
			(
				currentState === WorkflowState.PendingSolutionArchitectureApproval.value
				&& authenticatedUser.isInRole(UserRole.PORSubToOnPremSolArqReviewer)
			) ||
			(
				currentState === WorkflowState.PendingForCyberApproval.value
				&& authenticatedUser.isInRole(UserRole.PORSubToOnPremCyberReviewer)
			)
		);
	}

	canSendToReview(subscription: SubscriptionRequest, workflowInstance: WorkflowInstance): boolean {
		const currentState = this.workflowInstance.currentWorkflowInstanceState.workflowState;
		return  ((
				((currentState === WorkflowState.PendingSubscriptionOwnerApproval || currentState === WorkflowState.PendingForCloudNetworkApproval || currentState === WorkflowState.PendingForPolicyTeamApproval) &&
				subscription.owner?.id === authenticatedUser.userId)
				
			) ||
			(
				currentState === WorkflowState.PendingForPolicyTeamApproval &&
				authenticatedUser.isInRole(UserRole.SubToInternetTechnicalReviewer)
			) ||
			(
				currentState === WorkflowState.PendingSolutionArchitectureApproval &&
				authenticatedUser.isInRole(UserRole.PORSubToInternetSolArqReviewer)
			) ||
			(
				currentState === WorkflowState.PendingForCyberApproval &&
				authenticatedUser.isInRole(UserRole.PORSubToInternetCyberReviewer)
			) ||
			(
				currentState === WorkflowState.PendingSolutionArchitectureApproval
				&& authenticatedUser.isInRole(UserRole.PORSubToOnPremSolArqReviewer)
			) ||
			(
				currentState === WorkflowState.PendingForCyberApproval
				&& authenticatedUser.isInRole(UserRole.PORSubToOnPremCyberReviewer)
			) &&
				currentState != WorkflowState.Review
			)
	}

	canHaveSubscriptionOwnerDecision(subscription: SubscriptionRequest, hasCustodianApproval: boolean): boolean {
		const currentState = this.workflowInstance.currentWorkflowInstanceState.workflowState;
		if (hasCustodianApproval){
			return currentState === WorkflowState.PendingSubscriptionOwnerApproval && (
				subscription.owner?.id === authenticatedUser.userId ||
				subscription.custodian?.id === authenticatedUser.userId ||
				subscription.custodianTwo?.id === authenticatedUser.userId
			);
		}
		return (subscription.owner?.id === authenticatedUser.userId &&
				(currentState === WorkflowState.PendingSubscriptionOwnerApproval));
	}

	canHaveEscalateDecision(): boolean {
		const currentState = this.workflowInstance.currentWorkflowInstanceState.workflowState.value;
		return (
			(authenticatedUser.isInRole(UserRole.SubToInternetTechnicalReviewer) && currentState === WorkflowState.PendingForPolicyTeamApproval.value) ||
			(authenticatedUser.isInRole(UserRole.SubToOnPremTechnicalReviewer) && currentState === WorkflowState.PendingForCloudNetworkApproval.value) ||
			(authenticatedUser.isInRole(UserRole.PORSubToInternetSolArqReviewer) && currentState === WorkflowState.PendingSolutionArchitectureApproval.value) ||
			(authenticatedUser.isInRole(UserRole.PORSubToOnPremSolArqReviewer) && currentState === WorkflowState.PendingSolutionArchitectureApproval.value)
		);
	}

	isEditable(): boolean {
		const currentState = this.workflowInstance.currentWorkflowInstanceState.workflowState.value;
		return (
			authenticatedUser.userId === this.workflowInstance.createdBy.id &&
			[
				WorkflowState.Draft.value,
				WorkflowState.Review.value,
			].includes(currentState)
		);
	}

	architectApprovalBypassed(): boolean {
		const workflowInstanceStates = this.workflowInstance.workflowInstanceStates;
		return (workflowInstanceStates.find((workflowInstanceState) => workflowInstanceState.workflowState.value == WorkflowState.PendingSolutionArchitectureApproval.value) == undefined) && !this.escalated;
	}

	cyberApprovalBypassed(): boolean {
		const workflowInstanceStates = this.workflowInstance.workflowInstanceStates;
		return (workflowInstanceStates.find((workflowInstanceState) => workflowInstanceState.workflowState.value == WorkflowState.PendingForCyberApproval.value) == undefined) && !this.escalated;
	}

}